import React from 'react';
import { useNavigate } from 'react-router-dom';
import { faChartPie } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { getLetters } from '@monorepo/shared/src/util/text-util';
import { FONT_GSS_FUTURA_SEMI_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { AppRoutes, ActivityTypes } from '@monorepo/shared/src/types';
import {
  Character,
  Student,
  StudentProgress,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import UseAssignmentStore from 'graph/state/query-hooks/use-assignments-store';
import OverallProgressCard from '../common/OverallProgressCard';
import { getAttemptFiles } from '../../utils/reports-util';
import TurnedInWorkSection from './TurnedInWorkSection';

const t = (key: string, options?) => translate('reports', key, options);

type Props = {
  student: Student;
  character?: Character;
  progressData?: StudentProgress[];
};

const CharacterReportingCard: React.FC<Props> = ({ student, character, progressData }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const letter = character && getLetters(character.character);

  const data = progressData?.find((progress) => progress?.student?.id === student?.id);
  const [{ assignments }] = UseAssignmentStore();
  const files = getAttemptFiles({
    assignments,
    studentId: student?.id,
    char: character?.character,
    activityType: ActivityTypes.PRACTICE_BOOK,
  });

  const progress = data?.characterProgress.find(
    (progress) => progress.characterId === character?.id
  );

  const redirect = () => {
    navigate(`/${AppRoutes.REPORTS}`);
  };

  return (
    <Box component={Paper} className={classes.card}>
      <Box display="flex" gap={remCalc(8)}>
        <Typography className={`${classes.cardTitle} noPrint`}>
          {t('reportingForLetter', { letter })}
        </Typography>
        <Typography className={`${classes.cardTitle} printOnly`}>
          {' '}
          {`${student?.firstName} ${student?.lastName}`}
        </Typography>
        <Button color="primary" onClick={redirect} className="noPrint">
          <FontAwesomeIcon className={classes.buttonLinkIcon} icon={faChartPie} />
          <Typography color="primary" className={classes.buttonLinkText}>
            {t('viewOverallReporting')}
          </Typography>
        </Button>
      </Box>
      <OverallProgressCard progress={progress} isStudentProgress />
      <TurnedInWorkSection
        files={files}
        character={character}
        student={student}
        progressData={progressData}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.typography.pxToRem(12),
    pageBreakAfter: 'avoid',
    '@media print': {
      boxShadow: 'none',
    },
  },
  cardTitle: {
    fontSize: remCalc(28),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
  buttonLinkIcon: {
    fontSize: remCalc(16),
    marginRight: remCalc(10),
  },
  buttonLinkText: {
    fontSize: remCalc(14),
    lineHeight: remCalc(20),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
}));

export default CharacterReportingCard;
